import { LoadingScreen } from '@components/LoadingScreen';
import { GoogleLoginButton, MicrosoftLoginButton } from '@components/LoginButtons';
import { UnstyledLink } from '@components/UnstyledLink';
import { Button, Stack, Typography } from '@mui/material';
import { getTenantIdentifier } from '@utils/getTenantIdentifier';
import { useGetMe } from '@utils/useGetMe';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { useNotification } from '../../../utils/useNotification';
import { useAuthResultMessageFormatter } from '../../../utils/getAuthResultMessage';
import { displayTenantSwitcherAfterLoginState } from '../atoms';

export const LoginPage: React.FC = () => {
  const { formatMessage, locale } = useIntl();
  const formatAuthResultMessage = useAuthResultMessageFormatter();
  const [loggingInProvider, setLoggingInProvider] = useState<string>();
  const setDisplayTenantSwitcherAfterLoginState = useSetAtom(displayTenantSwitcherAfterLoginState);
  const [params, setParams] = useSearchParams();
  const [returnUrl, setReturnUrl] = useState<string>();

  const { notifyFailure, notifySuccess } = useNotification();

  const { me, isFetching, isFetched } = useGetMe();
  const signedIntoTenant = getTenantIdentifier();

  useEffect(() => {
    const returnUrlParam = params.get('returnUrl');
    if (returnUrlParam) {
      setParams((prevParams) => {
        prevParams.delete('returnUrl');
        return prevParams;
      });

      setReturnUrl(returnUrlParam);
      setDisplayTenantSwitcherAfterLoginState(false);
    } else if (!signedIntoTenant && !returnUrl) {
      setDisplayTenantSwitcherAfterLoginState(true);
    } else {
      setDisplayTenantSwitcherAfterLoginState(false);
    }
  }, [isFetched, me, notifyFailure, params, returnUrl, setDisplayTenantSwitcherAfterLoginState, setParams, signedIntoTenant]);

  useEffect(() => {
    const successParam = params.get('success');
    const code = params.get('code');
    const hasResult = successParam === 'true' || code != null;
    if (hasResult) {
      const success = successParam === 'true';
      const message = formatAuthResultMessage(success, code);
      if (message) {
        if (success) notifySuccess(message);
        else notifyFailure(message);
      }
      setParams((prevParams) => {
        prevParams.delete('success');
        prevParams.delete('code');
        return prevParams;
      });
    }
  }, [formatAuthResultMessage, notifyFailure, notifySuccess, params, setParams]);

  if (isFetching) {
    return <LoadingScreen />;
  }


  if (me != null && signedIntoTenant) {
    return <Navigate to={`/${getTenantIdentifier()}`} />;
  }

  return (
    <Stack flex={1} spacing={3} p={{ xs: 2, md: 4 }} maxWidth={600} mx="auto" width="100%">
      <Stack justifyContent='space-between' flex={1} gap={4}>
        <Stack spacing={3}>
      <Stack spacing={1} alignItems="center">
        <Typography variant="h3" color="primary.main">
          {formatMessage({ id: 'Welcome' })}
        </Typography>
        <Typography textAlign="center">
          {formatMessage({ id: 'Please identify yourself to access the application' })}
        </Typography>
      </Stack>

      <Stack spacing={2} alignItems="stretch" >
        <form id="external-account-ms" action="/api/Account/ExternalLogin" method="post">
          {returnUrl && <input name="returnUrl" type="hidden" value={returnUrl} />}
          <input type="hidden" name="language" value={locale} />
          <MicrosoftLoginButton
            loading={loggingInProvider === 'OpenIdConnect'}
            disabled={Boolean(loggingInProvider) && loggingInProvider !== 'OpenIdConnect'}
            fullWidth
            type="submit"
            name="provider"
            value="OpenIdConnect"
            onClickCapture={() => setLoggingInProvider('OpenIdConnect')}
          />
        </form>
        <form id="external-account-google" action="/api/Account/ExternalLogin" method="post">
          {returnUrl && <input name="returnUrl" type="hidden" value={returnUrl} />}
          <input type="hidden" name="language" value={locale} />
          <GoogleLoginButton
            fullWidth
            loading={loggingInProvider === 'Google'}
            disabled={Boolean(loggingInProvider) && loggingInProvider !== 'Google'}
            type="submit"
            name="provider"
            value="Google"
            onClickCapture={() => setLoggingInProvider('Google')}
          />
        </form>
      </Stack>
</Stack>
      <Stack p={2} spacing={1.5} sx={{ bgcolor: 'grey.100', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {formatMessage({ id: 'Ready to collaborate efficiently with your teamn?' })}
        </Typography>
        <UnstyledLink to="trial" sx={{ width: '100%' }}>
          <Button fullWidth variant="outlined" color="primary" size="medium">
            {formatMessage({ id: 'Start a trial for your organization' })}
          </Button>
        </UnstyledLink>
      </Stack></Stack>
    </Stack>
  );
};